import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DataGrid } from '@mui/x-data-grid';
import { Link, useNavigate } from 'react-router-dom';
import {
  cancelSubscriptionRequest,
  fetchSubscriptionsRequest,
  markSubscriptionCanceled,
  markSubscriptionCreated,
} from '../../api/subscriptions/subscriptionSlice';
import { fetchOrganizationById } from '../../api/organizations/organizationSlice';
import { fetchPlanById } from '../../api/plans/planSlice';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Modal,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { debounce } from 'lodash';
import { getStatusColor, getStatusLabel } from '../../utils/statues';
import { selectUser } from '../../api/auth/authSlice';
import appConfig from '../../config/appConfig';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

const SubscriptionTable = () => {
  const dispatch = useDispatch();
  const { subscriptions, totalCount, isSubscriptionCanceled, error, loading } = useSelector(
    (state) => state.subscriptions,
  );
  const organizations = useSelector((state) => state.organizations.organizations);
  const plans = useSelector((state) => state.plans.plans);
  const [page] = useState(0);
  const [limit] = useState(10);
  const [searchValue, setSearchValue] = useState('');
  const navigate = useNavigate();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const user = useSelector(selectUser);

  useEffect(() => {
    dispatch(markSubscriptionCreated(false));
    dispatch(fetchSubscriptionsRequest({ page: page + 1, limit: limit }));
  }, [dispatch, page, limit]);

  useEffect(() => {
    subscriptions.forEach((subscription) => {
      if (subscription.organization_id) {
        dispatch(fetchOrganizationById(subscription.organization_id));
      }
      if (subscription.plan_id) {
        dispatch(fetchPlanById(subscription.plan_id));
      }
    });
  }, [subscriptions, dispatch]);

  const handleEdit = (id) => {
    navigate(`/subscriptions/edit/${id}`);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  useEffect(() => {
    if (isSubscriptionCanceled) {
      setSnackbarMessage('Subscription cancelled successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } else if (error) {
      setSnackbarMessage(`Failed to cancel subscription: ${error.id}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
    dispatch(markSubscriptionCanceled(false));
  }, [isSubscriptionCanceled, error, loading]);

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Create a separate component for the cell content
  const ActionCell = ({ params, handleEdit }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const { loading } = useSelector((state) => state.subscriptions);
    const [isProcessingCancel, setIsProcessingCancel] = useState(false);

    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const onEdit = () => {
      handleClose();
      handleEdit(params.row._id);
    };

    const openCancelDialog = () => {
      handleClose();
      setConfirmOpen(true);
    };

    const confirmCancel = () => {
      setIsProcessingCancel(true);
      dispatch(
        cancelSubscriptionRequest({
          id: params.row._id,
          reason: '',
          immediately: true,
        }),
      );
    };

    useEffect(() => {
      if (!loading && isProcessingCancel) {
        setConfirmOpen(false);
        setIsProcessingCancel(false);
      }
    }, [loading, confirmOpen]);

    return (
      <>
        <IconButton aria-label="more" aria-controls="long-menu" aria-haspopup="true" onClick={handleClick}>
          <MoreVertIcon />
        </IconButton>
        <Menu id="simple-menu" anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}>
          <MenuItem onClick={onEdit}>Edit</MenuItem>
          {params.row.status !== 'canceled' && <MenuItem onClick={openCancelDialog}>Cancel Subscription</MenuItem>}
        </Menu>

        {/* Confirmation Dialog */}
        <Modal
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
        >
          <Box sx={modalStyle}>
            <Typography id="modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
              Confirm Subscription Cancellation
            </Typography>
            <Typography id="modal-description" sx={{ mb: 3 }}>
              Are you sure you want to cancel this subscription? This action cannot be undone.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button onClick={() => setConfirmOpen(false)} variant="outlined">
                No
              </Button>
              <Button
                onClick={confirmCancel}
                variant="contained"
                color="error"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Canceling...' : 'Yes'}
              </Button>
            </Box>
          </Box>
        </Modal>
      </>
    );
  };

  const columns = useMemo(() => {
    const columns = [
      {
        field: 'organization',
        headerName: 'Organisation',
        flex: 3.5,
        renderCell: (params) => {
          const organization = organizations.find((org) => org._id === params.row?.organization_id);
          if (!organization) return '';

          return (
            <Link
              to={`/subscriptions/details/${params.row._id}`}
              style={{
                color: 'blue',
                textDecoration: 'underline',
              }}
            >
              {`${organization.name} (${organization.email})`}
            </Link>
          );
        },
      },
      {
        field: 'subscription_status',
        headerName: 'Status',
        flex: 2,
        renderCell: (params) => (
          <Chip label={getStatusLabel(params.row.status)} color={getStatusColor(params.row.status)} size="small" />
        ),
      },
      {
        field: 'plan',
        headerName: 'Plan',
        flex: 2,
        renderCell: (params) => {
          const plan = plans.find((p) => p._id === params.row?.plan_id);
          if (!plan) return '';
          return plan.name;
        },
      },
      {
        field: 'price',
        headerName: 'Price',
        flex: 2.7,
        renderCell: (params) => {
          const plan = plans.find((p) => p._id === params.row?.plan_id);
          if (!plan) return '';

          const getPrice = (plan) => {
            if (!plan) return '';
            let price = plan.price;
            let prefix = '';
            let suffix = '';
            if (plan.billing_schema === 'tiered' && plan.tiers?.[0]) {
              prefix = 'Starts at ';
              price = plan.tiers[0].unit_amount;
              suffix = ' per document';
            }

            return (
              prefix +
              new Intl.NumberFormat(window.navigator.language, {
                style: 'currency',
                currency: appConfig.stripeCurrency,
              }).format(price) +
              suffix
            );
          };

          return getPrice(plan);
        },
      },

      {
        field: 'billing_interval',
        headerName: 'Billing Interval',
        flex: 1.5,
        renderCell: (params) => {
          const plan = plans.find((p) => p._id === params.row?.plan_id);
          if (!plan) return '';
          const { value, unit } = plan.billing_interval;
          return `${value} ${unit}`;
        },
      },
      {
        field: 'documents_limit',
        headerName: 'Documents Limit',
        flex: 1.5,
        renderCell: (params) => {
          const plan = plans.find((p) => p._id === params.row?.plan_id);
          if (!plan) return '';
          return new Intl.NumberFormat(window.navigator.language).format(params.row.documents_limit);
        },
      },
    ];

    if (user?.role === 'admin') {
      columns.push({
        field: 'actions',
        headerName: 'Actions',
        flex: 1.7,
        sortable: false,
        renderCell: (params) => <ActionCell params={params} handleEdit={handleEdit} showSnackbar={showSnackbar} />,
      });
    }
    return columns;
  }, [organizations, plans]);

  const handleSearchChange = useCallback(
    debounce((e) => {
      setSearchValue(e.target.value);
      dispatch(
        fetchSubscriptionsRequest({
          page: 1,
          limit: 10,
          email: e.target.value,
        }),
      );
    }, 500), // Adjust debounce delay as needed (500ms)
    [dispatch],
  );

  return (
    <>
      <Stack
        sx={{
          justifyContent: 'flex-end',
          alignItems: 'end',
          mb: 1,
        }}
      >
        <TextField
          size={'small'}
          id={'search-email'}
          label="Search by email"
          variant="outlined"
          onChange={handleSearchChange}
        />
      </Stack>
      <DataGrid
        rows={subscriptions}
        columns={columns}
        getRowId={(row) => row._id}
        paginationMode="server"
        rowCount={totalCount}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: limit,
            },
          },
        }}
        pageSizeOptions={[10, 25]}
        disableRowSelectionOnClick
        onPaginationModelChange={(model) => {
          dispatch(
            fetchSubscriptionsRequest({
              page: model.page + 1,
              limit: model.pageSize,
              email: searchValue,
            }),
          );
        }}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SubscriptionTable;
